<template>
  <div v-if="hasError" class="error-boundary">
    <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-medium text-red-800">
            Something went wrong
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <p>{{ errorMessage }}</p>
            <p v-if="showDetails" class="mt-2 text-xs text-red-600 font-mono bg-red-100 p-2 rounded">
              {{ errorDetails }}
            </p>
          </div>
          <div class="mt-4 flex space-x-3">
            <button
              @click="retry"
              class="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Try Again
            </button>
            <button
              @click="showDetails = !showDetails"
              class="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              {{ showDetails ? 'Hide' : 'Show' }} Details
            </button>
            <button
              @click="reportError"
              class="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Report Issue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <slot v-else />
</template>

<script setup lang="ts">
import { ref, onErrorCaptured, onMounted } from 'vue'
import { errorLogger } from '../utils/errorLogger'
import { showError, showSuccess } from '../utils/notiflix'

interface Props {
  fallbackMessage?: string
  showRetry?: boolean
  onRetry?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  fallbackMessage: 'An unexpected error occurred. Please try again.',
  showRetry: true
})

const hasError = ref(false)
const errorMessage = ref('')
const errorDetails = ref('')
const showDetails = ref(false)
const originalError = ref<Error | null>(null)

// Capture Vue component errors
onErrorCaptured((error: Error, instance, info) => {
  console.error('ErrorBoundary caught error:', error, info)
  
  handleError(error, {
    component: instance?.$?.type?.name || 'Unknown',
    info,
    timestamp: new Date().toISOString()
  })
  
  return false // Prevent error from propagating
})

// Capture global JavaScript errors
onMounted(() => {
  const handleGlobalError = (event: ErrorEvent) => {
    console.error('Global error caught:', event.error)
    handleError(event.error || new Error(event.message), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString()
    })
  }

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    console.error('Unhandled promise rejection:', event.reason)
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    handleError(error, {
      type: 'unhandledRejection',
      timestamp: new Date().toISOString()
    })
  }

  window.addEventListener('error', handleGlobalError)
  window.addEventListener('unhandledrejection', handleUnhandledRejection)

  // Cleanup listeners when component unmounts
  return () => {
    window.removeEventListener('error', handleGlobalError)
    window.removeEventListener('unhandledrejection', handleUnhandledRejection)
  }
})

const handleError = (error: Error, context: Record<string, any> = {}) => {
  originalError.value = error
  hasError.value = true
  
  // Set user-friendly error message
  errorMessage.value = getUserFriendlyMessage(error)
  errorDetails.value = `${error.name}: ${error.message}\n${error.stack || 'No stack trace available'}`
  
  // Log error for debugging and reporting
  errorLogger.logError(error, context)
  
  // Show notification for non-critical errors
  if (!isCriticalError(error)) {
    showError(errorMessage.value)
  }
}

const getUserFriendlyMessage = (error: Error): string => {
  // Network errors
  if (error.message.includes('fetch') || error.message.includes('network')) {
    return 'Network connection issue. Please check your internet connection and try again.'
  }
  
  // Authentication errors
  if (error.message.includes('auth') || error.message.includes('unauthorized')) {
    return 'Authentication error. Please refresh the page and try again.'
  }
  
  // Validation errors
  if (error.message.includes('validation') || error.message.includes('invalid')) {
    return error.message // These are usually user-friendly already
  }
  
  // Conflict errors
  if (error.message.includes('conflict')) {
    return error.message // These are usually user-friendly already
  }
  
  // Database errors
  if (error.message.includes('database') || error.message.includes('sql')) {
    return 'Database error. Please try again in a moment.'
  }
  
  // Default fallback
  return props.fallbackMessage
}

const isCriticalError = (error: Error): boolean => {
  // Critical errors that should show the error boundary instead of just notifications
  return (error.message?.includes('chunk') ?? false) || // Code splitting errors
         (error.message?.includes('module') ?? false) || // Module loading errors
         error.name === 'ChunkLoadError' ||
         (error.stack?.includes('dynamicImport') ?? false)
}

const retry = () => {
  hasError.value = false
  errorMessage.value = ''
  errorDetails.value = ''
  showDetails.value = false
  originalError.value = null
  
  if (props.onRetry) {
    props.onRetry()
  } else {
    // Default retry behavior - reload the page
    window.location.reload()
  }
}

const reportError = () => {
  if (originalError.value) {
    errorLogger.reportError(originalError.value, {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    })
    
    showSuccess('รายงานข้อผิดพลาดถูกส่งแล้ว ขอบคุณที่ช่วยเราปรับปรุง!')
  }
}
</script>

<style scoped>
.error-boundary {
  padding: 2rem;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>